import { apiClient } from './api'
import type { ApiErrorLike } from '../utils/apiError'

export interface TimeEntry {
  id: number
  task_id: number
  user_id: number
  date: string
  hours: number
  description?: string | null
  billable: boolean
  started_at?: string | null
  ended_at?: string | null
}

export interface ProjectTimeSummary {
  project_id: number
  total_hours: number
  billable_hours: number
  by_user: Array<{ user_id: number; hours: number; billable_hours: number }>
}

export const timeTrackingService = {
  list: async (params: { task_id?: number; user_id?: number; start_date?: string; end_date?: string; billable?: boolean } = {}) => {
    const { data } = await apiClient.get<TimeEntry[]>('/api/v1/time-entries', { params })
    return data
  },

  create: async (body: { task_id: number; date: string; hours: number; description?: string; billable?: boolean }) => {
    const { data } = await apiClient.post<TimeEntry>('/api/v1/time-entries', body)
    return data
  },

  update: async (id: number, body: Partial<Omit<TimeEntry, 'id' | 'task_id' | 'user_id'>>) => {
    const { data } = await apiClient.put<TimeEntry>(`/api/v1/time-entries/${id}`, body)
    return data
  },

  remove: async (id: number) => {
    await apiClient.delete(`/api/v1/time-entries/${id}`)
  },

  running: async (): Promise<TimeEntry | null> => {
    try {
      const { data } = await apiClient.get<TimeEntry | null>('/api/v1/time-entries/running')
      return data ?? null
    } catch (eRaw) {
      const e = eRaw as ApiErrorLike
      if (e?.response?.status === 404) return null
      throw e
    }
  },

  startTimer: async (taskId: number, body: { description?: string; billable?: boolean } = {}) => {
    const { data } = await apiClient.post<TimeEntry>(`/api/v1/tasks/${taskId}/timer/start`, body)
    return data
  },

  stopTimer: async (entryId: number) => {
    const { data } = await apiClient.post<TimeEntry>(`/api/v1/time-entries/${entryId}/stop`)
    return data
  },

  projectSummary: async (projectId: number, params: { start_date?: string; end_date?: string } = {}) => {
    const { data } = await apiClient.get<ProjectTimeSummary>(
      `/api/v1/projects/${projectId}/time-summary`,
      { params },
    )
    return data
  },
}
